import type { DailyChartMetric, ModalPropTypes } from './DailyChart.type';
import type { ChartOptionsType } from '@rango-dev/charts';
import type { CSSProperties, ReactElement } from 'react';

import { BarChart, prepareBarChartData } from '@rango-dev/charts';
import { Box, Flex, Grid, SegmentedControl, Text } from '@rango-dev/whale';
import ParentSize from '@visx/responsive/lib/components/ParentSize';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '../../../../hooks/reduxHooks';
import { capitalizeFirstLetter } from '../../../../utils/generalFunctions/common';
import { Dialog } from '../../../Dialog';

import { barChartColors, createChartDataTabs } from './DailyChart.helper';
import styles from './DailyChart.module.css';
import { DailyChartTabs } from './DailyChart.type';

const RECENT_DAILY_LIMIT = 30;
const MOBILE_CHART_HEIGHT = '225px';
const DESKTOP_CHART_HEIGHT = '400px';

export function DailyChartModal(props: ModalPropTypes): ReactElement {
  const { open, onClose, dailyData } = props;
  const { t } = useTranslation();
  const chartDataTabs = createChartDataTabs(dailyData);
  const [selectedTab, setSelectedTab] = useState<DailyChartTabs>(chartDataTabs[0].id);
  const { isDarkMode } = useAppSelector((state) => state.settings);

  const chartOption: ChartOptionsType = {
    dailyData: dailyData.map((item) => ({
      date: item.date,
      bucket: capitalizeFirstLetter(item.bucket),
      value: (item as DailyChartMetric)[selectedTab],
    })),
    barChartColors,
  };

  const { buckets, chartData, colorBucketMap } = prepareBarChartData(chartOption);
  const displayedItems = chartData.slice(Math.max(chartData.length - RECENT_DAILY_LIMIT, 0));

  useEffect(() => {
    if (open) {
      setSelectedTab(chartDataTabs[0].id);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
      width="900px"
      scrollBody={false}
      idPrefix="profile-page-daily-chart-modal"
      title={t('Daily Progress Chart')}
      description={t('Swap volume and number from your dApps')}>
      <Flex direction="column" gap="5" width="100%">
        <Box width="100%" overflow="auto">
          <SegmentedControl.Root
            className={styles.segmentControl}
            size="2"
            value={selectedTab}
            onValueChange={(value) => setSelectedTab(value as DailyChartTabs)}>
            {chartDataTabs.map((tab) => (
              <SegmentedControl.Item key={tab.id} value={tab.id}>
                {tab.title}
              </SegmentedControl.Item>
            ))}
          </SegmentedControl.Root>
        </Box>

        <Box className={styles.chartLayout} width="100%">
          <Box
            className={styles.chartThemeIsolate}
            width="100%"
            minWidth="0"
            height={{ initial: MOBILE_CHART_HEIGHT, sm: DESKTOP_CHART_HEIGHT }}>
            <ParentSize>
              {({ width, height }) => (
                <BarChart
                  width={width}
                  height={height}
                  isDarkTheme={isDarkMode}
                  data={displayedItems}
                  buckets={buckets}
                  colorBucketMap={colorBucketMap}
                  getLabel={(value) =>
                    selectedTab === DailyChartTabs.NumOfSwaps ||
                    selectedTab === DailyChartTabs.NumOfEffectiveSwaps
                      ? `${value}`
                      : `$${value}`
                  }
                />
              )}
            </ParentSize>
          </Box>

          <Box
            className={styles.legendContainer}
            width="100%"
            px={{ initial: '0', sm: '4' }}
            py={{ initial: '0', sm: '2' }}>
            <Grid columns={{ initial: '3', sm: '1' }} gap={{ initial: '2', sm: '0' }}>
              {chartData?.length
                ? Array.from(colorBucketMap).map(([bucketItem, bucketColor]) => (
                    <Flex
                      key={bucketItem}
                      className={styles.legendRow}
                      align="center"
                      gap="1"
                      px={{ initial: '0', sm: '1' }}
                      py={{ initial: '0', sm: '2' }}>
                      <Box
                        className={styles.legendDot}
                        style={{ ['--dot']: bucketColor } as CSSProperties}
                      />
                      <Text
                        size={{ initial: '1', sm: '2' }}
                        weight={{ initial: 'light', sm: 'regular' }}
                        truncate>
                        {bucketItem}
                      </Text>
                    </Flex>
                  ))
                : null}
            </Grid>
          </Box>
        </Box>
      </Flex>
    </Dialog>
  );
}
